import { Box } from '@mui/material';
import { type JSX, type SyntheticEvent, useCallback } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useEventTracker, useTopHeader } from 'app/hooks';
import { BILLING_AND_REPORTS_EVENTS } from 'app/tracking/constants';
import { Tabs } from 'app/shared-components/structure';
import type { HealtheraTab } from 'app/shared-components/structure/Tabs';
import FuseSuspense from '@/@fuse/core/FuseSuspense';

import { INVOICE_PERIOD_QUERY, REPORT_PERIOD_QUERY } from '../filterQueryKeys';
import SubscriptionManagementCard from '../use-cases/subscription-management';

const ORGANISATION_ID_QUERY = 'organisationId';

const tabs: HealtheraTab[] = [
  { label: 'Reports', value: 'reports' },
  { label: 'Billing', value: 'billing' },
];

const periodQueryFor = (tab: string): string =>
  tab === 'billing' ? INVOICE_PERIOD_QUERY : REPORT_PERIOD_QUERY;

export default function BillingAndReportsLayout(): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const trackEvent = useEventTracker('Billing and Reports');

  useTopHeader('Billing and Reports');

  const lastSegment = pathname.split('/').pop() ?? '';
  const selectedTab = lastSegment === 'billing' ? 'billing' : 'reports';

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, newTab: string) => {
      const tabEvent =
        newTab === 'billing'
          ? BILLING_AND_REPORTS_EVENTS.BILLING_TAB_SELECTED
          : BILLING_AND_REPORTS_EVENTS.REPORTS_TAB_SELECTED;
      trackEvent(tabEvent.action, tabEvent);

      const nextPathname = newTab === 'billing' ? '/documents/billing' : '/documents/reports';

      const nextParams = new URLSearchParams();

      const organisationId = searchParams.get(ORGANISATION_ID_QUERY);
      if (organisationId) nextParams.set(ORGANISATION_ID_QUERY, organisationId);

      const period = searchParams.get(periodQueryFor(selectedTab));
      if (period) nextParams.set(periodQueryFor(newTab), period);

      navigate({ pathname: nextPathname, search: nextParams.toString() });
    },
    [navigate, searchParams, selectedTab, trackEvent]
  );

  return (
    <Box className="grow flex flex-col overflow-hidden px-20 py-16 gap-16">
      <SubscriptionManagementCard />

      <Tabs tabs={tabs} selectedTab={selectedTab} handleChange={handleTabChange} />

      <FuseSuspense>
        <Outlet />
      </FuseSuspense>
    </Box>
  );
}
